import { useState, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core"
import { useUpdateTask } from "@/hooks/use-tasks"
import { Badge } from "@/components/ui/badge"
import { Calendar, CheckSquare, User } from "lucide-react"
import { formatDate } from "@/lib/utils"
import { toast } from "sonner"
import type { TaskItem, TaskStatus } from "@/lib/types"

// ── Column definitions ───────────────────────────────────────────

interface StatusColumn {
  id: TaskStatus
  label: string
  color: string
}

const STATUS_COLUMNS: StatusColumn[] = [
  { id: "open", label: "Otwarte", color: "#3B82F6" },
  { id: "in_progress", label: "W toku", color: "#F59E0B" },
  { id: "completed", label: "Ukończone", color: "#22C55E" },
  { id: "cancelled", label: "Anulowane", color: "#6B7280" },
]

const PRIORITY_COLORS: Record<string, string> = {
  low: "#6B7280",
  normal: "var(--primary)",
  high: "#F59E0B",
  urgent: "#EF4444",
}

const PRIORITY_LABELS: Record<string, string> = {
  low: "Niski",
  normal: "Normalny",
  high: "Wysoki",
  urgent: "Pilny",
}

// ── Task Card ────────────────────────────────────────────────────

function TaskCardContent({
  task,
  onClick,
  isDragOverlay,
}: {
  task: TaskItem
  onClick?: () => void
  isDragOverlay?: boolean
}) {
  const isOverdue =
    task.due_date &&
    new Date(task.due_date) < new Date() &&
    task.status !== "completed"

  return (
    <div
      onClick={onClick}
      className={`rounded-lg border p-3 transition-shadow cursor-pointer bg-card ${
        isDragOverlay
          ? "border-primary shadow-lg opacity-95"
          : "shadow-sm hover:shadow-md"
      }`}
      style={isDragOverlay ? { width: 260 } : undefined}
    >
      {/* Title */}
      <p
        className={`text-sm font-medium leading-snug mb-1.5 line-clamp-2 ${
          task.status === "completed"
            ? "line-through text-muted-foreground"
            : "text-foreground"
        }`}
      >
        {task.title}
      </p>

      {/* Priority badge */}
      <div className="flex items-center gap-1.5 mb-2">
        <Badge
          variant="outline"
          className="text-[10px] px-1.5 py-0"
          style={{
            borderColor: PRIORITY_COLORS[task.priority],
            color: PRIORITY_COLORS[task.priority],
          }}
        >
          {PRIORITY_LABELS[task.priority]}
        </Badge>

        {/* Category dot */}
        {task.task_category && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-1.5 py-0 text-[10px] font-medium"
            style={{
              backgroundColor: task.task_category.color + "18",
              color: task.task_category.color,
            }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: task.task_category.color }}
            />
            {task.task_category.name}
          </span>
        )}
      </div>

      {/* Due date */}
      {task.due_date && (
        <div className="flex items-center gap-1 mb-2">
          <Calendar size={11} className={isOverdue ? "text-red-500" : "text-muted-foreground"} />
          <span
            className={`text-xs ${isOverdue ? "text-red-500 font-medium" : "text-muted-foreground"}`}
          >
            {formatDate(task.due_date)}
            {task.due_time && <span className="ml-1">{task.due_time}</span>}
          </span>
        </div>
      )}

      {/* Checklist progress */}
      {task.checklist_progress.total > 0 && (
        <div className="flex items-center gap-1 mb-2">
          <CheckSquare size={11} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {task.checklist_progress.completed}/{task.checklist_progress.total}
          </span>
        </div>
      )}

      {/* Footer: assignee */}
      <div className="flex items-center justify-between">
        {task.assignee ? (
          <div className="flex items-center gap-1.5">
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
              {task.assignee.first_name[0]}
              {task.assignee.last_name[0]}
            </div>
            <span className="text-xs text-muted-foreground">
              {task.assignee.first_name} {task.assignee.last_name[0]}.
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-muted-foreground">
            <User size={12} />
            <span className="text-xs">—</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Draggable Card Wrapper ───────────────────────────────────────

function DraggableTaskCard({
  task,
  onCardClick,
}: {
  task: TaskItem
  onCardClick: (id: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: task.id })

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        opacity: isDragging ? 0.3 : 1,
        transition: isDragging ? undefined : "opacity 150ms ease",
      }}
      {...listeners}
      {...attributes}
    >
      <TaskCardContent task={task} onClick={() => onCardClick(task.id)} />
    </div>
  )
}

// ── Kanban Column ────────────────────────────────────────────────

function TaskKanbanColumn({
  column,
  tasks,
  onCardClick,
}: {
  column: StatusColumn
  tasks: TaskItem[]
  onCardClick: (id: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id })

  return (
    <div className="flex flex-col min-w-[260px] max-w-[320px] flex-1 min-h-[200px]">
      {/* Column Header */}
      <div className="flex items-center gap-2 rounded-t-lg px-3 py-2.5 border border-b-0 border-border bg-muted">
        <div
          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: column.color }}
        />
        <span className="text-sm font-semibold truncate text-foreground">
          {column.label}
        </span>
        <span
          className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
            tasks.length > 0 ? "" : "bg-muted text-muted-foreground"
          }`}
          style={
            tasks.length > 0
              ? {
                  backgroundColor: column.color + "18",
                  color: column.color,
                }
              : undefined
          }
        >
          {tasks.length}
        </span>
      </div>

      {/* Cards area */}
      <div
        ref={setNodeRef}
        className="flex-1 space-y-2 rounded-b-lg border border-border p-2 overflow-y-auto transition-colors"
        style={{
          borderColor: isOver ? column.color : undefined,
          backgroundColor: isOver ? column.color + "08" : "transparent",
          maxHeight: "calc(100vh - 380px)",
        }}
      >
        {tasks.map((task) => (
          <DraggableTaskCard
            key={task.id}
            task={task}
            onCardClick={onCardClick}
          />
        ))}

        {tasks.length === 0 && (
          <div className="flex items-center justify-center rounded-md border border-dashed border-border py-8 text-xs text-muted-foreground">
            Brak zadań
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Board Component ─────────────────────────────────────────

interface TaskKanbanBoardProps {
  tasks: TaskItem[]
}

export function TaskKanbanBoard({ tasks }: TaskKanbanBoardProps) {
  const navigate = useNavigate()
  const updateTaskMutation = useUpdateTask()

  // Group tasks by status
  const grouped = useMemo(() => {
    const map: Record<TaskStatus, TaskItem[]> = {
      open: [],
      in_progress: [],
      completed: [],
      cancelled: [],
    }
    for (const task of tasks) {
      if (map[task.status]) {
        map[task.status].push(task)
      }
    }
    return map
  }, [tasks])

  // DnD state
  const [activeCard, setActiveCard] = useState<TaskItem | null>(null)
  const [localGrouped, setLocalGrouped] = useState<Record<
    TaskStatus,
    TaskItem[]
  > | null>(null)

  const displayGrouped = localGrouped ?? grouped

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  )

  const findColumnForCard = useCallback(
    (
      cardId: string,
      groups: Record<TaskStatus, TaskItem[]>,
    ): TaskStatus | null => {
      for (const [status, items] of Object.entries(groups)) {
        if (items.some((t) => t.id === cardId)) return status as TaskStatus
      }
      return null
    },
    [],
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = String(event.active.id)
      for (const items of Object.values(grouped)) {
        const card = items.find((t) => t.id === id)
        if (card) {
          setActiveCard(card)
          setLocalGrouped(
            Object.fromEntries(
              Object.entries(grouped).map(([k, v]) => [k, [...v]]),
            ) as Record<TaskStatus, TaskItem[]>,
          )
          break
        }
      }
    },
    [grouped],
  )

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event
      if (!over || !localGrouped) return

      const activeId = String(active.id)
      const overId = String(over.id)

      const fromCol = findColumnForCard(activeId, localGrouped)
      let toCol = findColumnForCard(overId, localGrouped)
      if (!toCol) {
        // Check if overId is a column status id
        if (overId in localGrouped) toCol = overId as TaskStatus
      }

      if (!fromCol || !toCol || fromCol === toCol) return

      setLocalGrouped((prev) => {
        if (!prev) return prev
        const next = Object.fromEntries(
          Object.entries(prev).map(([k, v]) => [k, [...v]]),
        ) as Record<TaskStatus, TaskItem[]>

        const cardIdx = next[fromCol].findIndex((t) => t.id === activeId)
        if (cardIdx === -1) return prev
        const [card] = next[fromCol].splice(cardIdx, 1)
        next[toCol].push(card)

        return next
      })
    },
    [localGrouped, findColumnForCard],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveCard(null)

      if (!over || !localGrouped) {
        setLocalGrouped(null)
        return
      }

      const cardId = String(active.id)
      const newStatus = findColumnForCard(cardId, localGrouped)
      const oldStatus = findColumnForCard(cardId, grouped)

      if (!newStatus || newStatus === oldStatus) {
        setLocalGrouped(null)
        return
      }

      // Clear local state — let React Query refetch
      setLocalGrouped(null)

      updateTaskMutation.mutate(
        { id: cardId, data: { status: newStatus } },
        {
          onError: () => {
            toast.error("Nie udało się zmienić statusu")
          },
        },
      )
    },
    [localGrouped, grouped, findColumnForCard, updateTaskMutation],
  )

  const handleDragCancel = useCallback(() => {
    setActiveCard(null)
    setLocalGrouped(null)
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-3 flex-1 overflow-x-auto pb-4">
        {STATUS_COLUMNS.map((col) => (
          <TaskKanbanColumn
            key={col.id}
            column={col}
            tasks={displayGrouped[col.id] ?? []}
            onCardClick={(id) => navigate(`/tasks/${id}`)}
          />
        ))}
      </div>

      <DragOverlay dropAnimation={null}>
        {activeCard ? (
          <TaskCardContent task={activeCard} isDragOverlay />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
